import React, { useEffect, useState } from "react";
import { getCreditAnalysisESGAnalysis } from "../../../../shared/services/apis/tenantAnalytics";
import IframeViewer from "../../../../shared/ui/urlOpener/IFrameViewer";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  HeadingLevel,
  Packer,
  convertInchesToTwip,
  TableLayoutType,
  PageOrientation,
  Footer,
  ImageRun,
} from "docx";
import { saveAs } from "file-saver";
import { RiDownloadLine } from "react-icons/ri";
import { getTenantById } from "../../../../shared/services/apis";

interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

interface ESGAnalysisProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

export const ESGAnalysis: React.FC<ESGAnalysisProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>(null);

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

  useEffect(() => {
    if (!tenantId) return;
    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };
    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getCreditAnalysisESGAnalysis(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response);
        setError(null);
      } catch {
        setError("Failed to load ESG analysis");
      } finally {
        setLoading(false);
      }
    };
    if (tenantId && tenantServiceId && tenantServiceOptionId) fetchData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  const downloadPDF = async () => {
    setDownloading(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const esg = data?.content?.esg_analysis;

      const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });
      };

      // Load watermark image if logoUrl exists
      let watermarkImg: HTMLImageElement | null = null;
      if (tenant?.logoUrl) {
        try {
          watermarkImg = await loadImage(tenant.logoUrl);
        } catch (error) {
          console.warn("Failed to load watermark image:", error);
        }
      }

      // Function to add watermark to a page
      const addWatermark = (pageNumber: number) => {
        if (!watermarkImg) return;

        doc.setPage(pageNumber);

        // Save graphics state and set transparency
        doc.saveGraphicsState();
        doc.setGState(doc.GState({ opacity: 0.1 })); // Remove 'new' keyword

        // Calculate watermark size and position (centered)
        const watermarkWidth = 80;
        const watermarkHeight =
          (watermarkImg.height / watermarkImg.width) * watermarkWidth;
        const xPos = (pageWidth - watermarkWidth) / 2;
        const yPos = (pageHeight - watermarkHeight) / 2;

        // Add watermark image
        doc.addImage(
          watermarkImg,
          "PNG",
          xPos,
          yPos,
          watermarkWidth,
          watermarkHeight
        );

        // Restore graphics state
        doc.restoreGraphicsState();
      };

      // Header
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("ESG Analysis", pageWidth / 2, 20, { align: "center" });

      // Date and time
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      doc.text(`Generated on ${currentDateTime}`, pageWidth / 2, 28, {
        align: "center",
      });

      let yPosition = 40;

      // Environmental Section
      if (esg?.environmental) {
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Environmental", 14, yPosition);
        yPosition += 8;

        // GHG Emissions
        if (
          esg.environmental.climate_change_carbon_emissions?.total_ghg_emissions
        ) {
          const ghg =
            esg.environmental.climate_change_carbon_emissions
              .total_ghg_emissions;

          doc.setFontSize(11);
          doc.text(`GHG Emissions (${ghg.year})`, 14, yPosition);
          yPosition += 6;

          autoTable(doc, {
            startY: yPosition,
            body: [
              ["Scope 1", ghg.scope_1],
              ["Scope 2", ghg.scope_2],
              ["Scope 3", ghg.scope_3],
              ["Total", ghg.total],
            ],
            theme: "grid",
            styles: { fontSize: 9, cellPadding: 2 },
            columnStyles: {
              0: { cellWidth: 50, fontStyle: "bold" },
              1: { cellWidth: "auto" },
            },
            margin: { left: 14, right: 14 },
          });

          yPosition = (doc as any).lastAutoTable.finalY + 8;
        }

        // Net Zero Commitment
        if (
          esg.environmental.climate_change_carbon_emissions
            ?.carbon_reduction_targets?.net_zero_commitment
        ) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.text("Net Zero Commitment", 14, yPosition);
          yPosition += 6;

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          const commitment = doc.splitTextToSize(
            esg.environmental.climate_change_carbon_emissions
              .carbon_reduction_targets.net_zero_commitment,
            pageWidth - 28
          );
          doc.text(commitment, 14, yPosition);
          yPosition += commitment.length * 4 + 8;
        }
      }

      // Social Section
      if (esg?.social) {
        if (yPosition > pageHeight - 80) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Social", 14, yPosition);
        yPosition += 8;

        const socialData = [];

        if (esg.social.workforce_labor_practices?.total_employees) {
          socialData.push([
            "Total Employees",
            esg.social.workforce_labor_practices.total_employees,
          ]);
        }

        if (esg.social.workforce_labor_practices?.gender_diversity) {
          const gd = esg.social.workforce_labor_practices.gender_diversity;
          socialData.push([
            "Female Employees %",
            gd.female_employees_percentage || "N/A",
          ]);
          socialData.push([
            "Female Leadership %",
            gd.female_leadership_percentage || "N/A",
          ]);
        }

        if (esg.social.health_safety?.safety_performance) {
          const sp = esg.social.health_safety.safety_performance;
          socialData.push(["LTIFR", sp.ltifr || "N/A"]);
          socialData.push(["TRIR", sp.trir || "N/A"]);
          socialData.push(["Fatalities", sp.fatalities || "N/A"]);
        }

        if (socialData.length > 0) {
          autoTable(doc, {
            startY: yPosition,
            body: socialData,
            theme: "grid",
            styles: { fontSize: 9, cellPadding: 2 },
            columnStyles: {
              0: { cellWidth: 60, fontStyle: "bold" },
              1: { cellWidth: "auto" },
            },
            margin: { left: 14, right: 14 },
          });

          yPosition = (doc as any).lastAutoTable.finalY + 8;
        }
      }

      // Governance Section
      if (esg?.governance?.board_structure) {
        if (yPosition > pageHeight - 60) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Governance", 14, yPosition);
        yPosition += 8;

        const govData = [
          ["Board Size", esg.governance.board_structure.board_size || "N/A"],
          [
            "Independent Directors",
            esg.governance.board_structure.independent_directors || "N/A",
          ],
        ];

        if (esg.governance.board_structure.board_committees) {
          govData.push([
            "Board Committees",
            esg.governance.board_structure.board_committees,
          ]);
        }

        autoTable(doc, {
          startY: yPosition,
          body: govData,
          theme: "grid",
          styles: { fontSize: 9, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 60, fontStyle: "bold" },
            1: { cellWidth: "auto" },
          },
          margin: { left: 14, right: 14 },
        });

        yPosition = (doc as any).lastAutoTable.finalY + 8;
      }

      // ESG Ratings
      if (esg?.esg_ratings_scores?.external_esg_ratings) {
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("ESG Ratings", 14, yPosition);
        yPosition += 8;

        const ratingsData = esg.esg_ratings_scores.external_esg_ratings.map(
          (rating: any) => [
            rating.rating_agency,
            rating.score_rating,
            rating.year,
          ]
        );

        autoTable(doc, {
          startY: yPosition,
          head: [["Rating Agency", "Score", "Year"]],
          body: ratingsData,
          theme: "grid",
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: {
            fillColor: [243, 244, 246],
            textColor: [55, 65, 81],
            fontStyle: "bold",
          },
          margin: { left: 14, right: 14 },
        });
      }

      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        addWatermark(i);
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      }

      const fileName = `ESG_Analysis_${new Date().getTime()}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const downloadWord = async () => {
    setDownloadingWord(true);

    try {
      const base64ToUint8Array = (base64: string): Uint8Array => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };
      // Load logo image if logoUrl exists
      let logoImageData: {
        data: Uint8Array;
        mimeType: "jpg" | "png" | "gif" | "bmp";
      } | null = null;
      if (tenant?.logoUrl) {
        try {
          console.log("Loading logo from:", tenant.logoUrl);
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();

          // Map MIME type to docx accepted format
          const getMimeType = (
            blobType: string
          ): "jpg" | "png" | "gif" | "bmp" => {
            if (blobType.includes("png")) return "png";
            if (blobType.includes("jpeg") || blobType.includes("jpg"))
              return "jpg";
            if (blobType.includes("gif")) return "gif";
            if (blobType.includes("bmp")) return "bmp";
            return "png";
          };

          const mimeType = getMimeType(blob.type);
          console.log("Detected MIME type:", mimeType);

          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const uint8Array = base64ToUint8Array(base64);
          console.log("Logo loaded, size:", uint8Array.length, "bytes");

          logoImageData = { data: uint8Array, mimeType };
        } catch (error) {
          console.error("Failed to load logo image:", error);
        }
      }

      const esg = data?.content?.esg_analysis;
      const children: (Paragraph | Table)[] = [];

      const pageWidth = convertInchesToTwip(8.5);
      const marginLeft = convertInchesToTwip(0.75);
      const marginRight = convertInchesToTwip(0.75);
      const availableWidth = pageWidth - marginLeft - marginRight;
      const col1Width = availableWidth * 0.4;
      const col2Width = availableWidth * 0.6;

      // Header
      children.push(
        new Paragraph({
          text: "ESG Analysis",
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 200, before: 200 },
        })
      );

      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Generated on ${currentDateTime}`,
              size: 20,
              color: "666666",
              italics: true,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 600 },
        })
      );

      // Environmental Section
      if (esg?.environmental) {
        children.push(
          new Paragraph({
            text: "Environmental",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        // GHG Emissions Table
        if (
          esg.environmental.climate_change_carbon_emissions?.total_ghg_emissions
        ) {
          const ghg =
            esg.environmental.climate_change_carbon_emissions
              .total_ghg_emissions;

          children.push(
            new Paragraph({
              text: `GHG Emissions (${ghg.year})`,
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 },
            })
          );

          const ghgTable = new Table({
            rows: [
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({ text: "Scope 1" })],
                    width: { size: col1Width, type: WidthType.DXA },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                  new TableCell({
                    children: [new Paragraph({ text: ghg.scope_1 })],
                    width: { size: col2Width, type: WidthType.DXA },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                ],
              }),
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({ text: "Scope 2" })],
                    width: { size: col1Width, type: WidthType.DXA },
                    shading: { fill: "F8F9FA" },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                  new TableCell({
                    children: [new Paragraph({ text: ghg.scope_2 })],
                    width: { size: col2Width, type: WidthType.DXA },
                    shading: { fill: "F8F9FA" },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                ],
              }),
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({ text: "Scope 3" })],
                    width: { size: col1Width, type: WidthType.DXA },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                  new TableCell({
                    children: [new Paragraph({ text: ghg.scope_3 })],
                    width: { size: col2Width, type: WidthType.DXA },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                ],
              }),
              new TableRow({
                children: [
                  new TableCell({
                    children: [
                      new Paragraph({
                        children: [new TextRun({ text: "Total", bold: true })],
                      }),
                    ],
                    width: { size: col1Width, type: WidthType.DXA },
                    shading: { fill: "F8F9FA" },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                  new TableCell({
                    children: [
                      new Paragraph({
                        children: [
                          new TextRun({ text: ghg.total, bold: true }),
                        ],
                      }),
                    ],
                    width: { size: col2Width, type: WidthType.DXA },
                    shading: { fill: "F8F9FA" },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                ],
              }),
            ],
            width: { size: availableWidth, type: WidthType.DXA },
            columnWidths: [col1Width, col2Width],
            layout: TableLayoutType.FIXED,
            borders: {
              top: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              bottom: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              left: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              right: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              insideHorizontal: {
                style: BorderStyle.SINGLE,
                size: 1,
                color: "DDDDDD",
              },
              insideVertical: {
                style: BorderStyle.SINGLE,
                size: 1,
                color: "DDDDDD",
              },
            },
          });

          children.push(ghgTable);
          children.push(new Paragraph({ text: "", spacing: { after: 200 } }));
        }

        // Net Zero Commitment
        if (
          esg.environmental.climate_change_carbon_emissions
            ?.carbon_reduction_targets?.net_zero_commitment
        ) {
          children.push(
            new Paragraph({
              text: "Net Zero Commitment",
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 },
            })
          );

          children.push(
            new Paragraph({
              text: esg.environmental.climate_change_carbon_emissions
                .carbon_reduction_targets.net_zero_commitment,
              spacing: { after: 200 },
              alignment: AlignmentType.JUSTIFIED,
            })
          );
        }
      }

      // Social Section
      if (esg?.social) {
        children.push(
          new Paragraph({
            text: "Social",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        const socialRows: any[] = [];

        if (esg.social.workforce_labor_practices?.total_employees) {
          socialRows.push([
            "Total Employees",
            esg.social.workforce_labor_practices.total_employees,
          ]);
        }

        if (esg.social.workforce_labor_practices?.gender_diversity) {
          const gd = esg.social.workforce_labor_practices.gender_diversity;
          socialRows.push([
            "Female Employees %",
            gd.female_employees_percentage || "N/A",
          ]);
          socialRows.push([
            "Female Leadership %",
            gd.female_leadership_percentage || "N/A",
          ]);
        }

        if (esg.social.health_safety?.safety_performance) {
          const sp = esg.social.health_safety.safety_performance;
          socialRows.push(["LTIFR", sp.ltifr || "N/A"]);
          socialRows.push(["TRIR", sp.trir || "N/A"]);
          socialRows.push(["Fatalities", sp.fatalities || "N/A"]);
        }

        if (socialRows.length > 0) {
          const socialTableRows = socialRows.map(
            ([label, value], idx) =>
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({ text: label })],
                    width: { size: col1Width, type: WidthType.DXA },
                    shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                  new TableCell({
                    children: [new Paragraph({ text: value })],
                    width: { size: col2Width, type: WidthType.DXA },
                    shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                ],
              })
          );

          const socialTable = new Table({
            rows: socialTableRows,
            width: { size: availableWidth, type: WidthType.DXA },
            columnWidths: [col1Width, col2Width],
            layout: TableLayoutType.FIXED,
            borders: {
              top: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              bottom: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              left: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              right: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              insideHorizontal: {
                style: BorderStyle.SINGLE,
                size: 1,
                color: "DDDDDD",
              },
              insideVertical: {
                style: BorderStyle.SINGLE,
                size: 1,
                color: "DDDDDD",
              },
            },
          });

          children.push(socialTable);
          children.push(new Paragraph({ text: "", spacing: { after: 200 } }));
        }
      }

      // Governance Section
      if (esg?.governance?.board_structure) {
        children.push(
          new Paragraph({
            text: "Governance",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        const govRows: any[] = [
          ["Board Size", esg.governance.board_structure.board_size || "N/A"],
          [
            "Independent Directors",
            esg.governance.board_structure.independent_directors || "N/A",
          ],
        ];

        if (esg.governance.board_structure.board_committees) {
          govRows.push([
            "Board Committees",
            esg.governance.board_structure.board_committees,
          ]);
        }

        const govTableRows = govRows.map(
          ([label, value], idx) =>
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ text: label })],
                  width: { size: col1Width, type: WidthType.DXA },
                  shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                  margins: {
                    top: convertInchesToTwip(0.08),
                    bottom: convertInchesToTwip(0.08),
                    left: convertInchesToTwip(0.08),
                    right: convertInchesToTwip(0.08),
                  },
                }),
                new TableCell({
                  children: [new Paragraph({ text: value })],
                  width: { size: col2Width, type: WidthType.DXA },
                  shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                  margins: {
                    top: convertInchesToTwip(0.08),
                    bottom: convertInchesToTwip(0.08),
                    left: convertInchesToTwip(0.08),
                    right: convertInchesToTwip(0.08),
                  },
                }),
              ],
            })
        );

        const govTable = new Table({
          rows: govTableRows,
          width: { size: availableWidth, type: WidthType.DXA },
          columnWidths: [col1Width, col2Width],
          layout: TableLayoutType.FIXED,
          borders: {
            top: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            bottom: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            left: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            right: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            insideHorizontal: {
              style: BorderStyle.SINGLE,
              size: 1,
              color: "DDDDDD",
            },
            insideVertical: {
              style: BorderStyle.SINGLE,
              size: 1,
              color: "DDDDDD",
            },
          },
        });

        children.push(govTable);
        children.push(new Paragraph({ text: "", spacing: { after: 200 } }));
      }

      // ESG Ratings
      if (esg?.esg_ratings_scores?.external_esg_ratings) {
        children.push(
          new Paragraph({
            text: "ESG Ratings",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        const col1 = availableWidth * 0.4;
        const col2 = availableWidth * 0.3;
        const col3 = availableWidth * 0.3;

        const ratingRows = [
          new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "Rating Agency",
                        bold: true,
                        size: 20,
                        color: "FFFFFF",
                      }),
                    ],
                  }),
                ],
                shading: { fill: "4472C4" },
                width: { size: col1, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "Score",
                        bold: true,
                        size: 20,
                        color: "FFFFFF",
                      }),
                    ],
                  }),
                ],
                shading: { fill: "4472C4" },
                width: { size: col2, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "Year",
                        bold: true,
                        size: 20,
                        color: "FFFFFF",
                      }),
                    ],
                  }),
                ],
                shading: { fill: "4472C4" },
                width: { size: col3, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
            ],
            tableHeader: true,
          }),
        ];

        esg.esg_ratings_scores.external_esg_ratings.forEach(
          (rating: any, idx: number) => {
            ratingRows.push(
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({ text: rating.rating_agency })],
                    width: { size: col1, type: WidthType.DXA },
                    shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                  new TableCell({
                    children: [new Paragraph({ text: rating.score_rating })],
                    width: { size: col2, type: WidthType.DXA },
                    shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                  new TableCell({
                    children: [new Paragraph({ text: rating.year })],
                    width: { size: col3, type: WidthType.DXA },
                    shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                ],
              })
            );
          }
        );

        const ratingsTable = new Table({
          rows: ratingRows,
          width: { size: availableWidth, type: WidthType.DXA },
          columnWidths: [col1, col2, col3],
          layout: TableLayoutType.FIXED,
          borders: {
            top: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            bottom: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            left: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            right: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            insideHorizontal: {
              style: BorderStyle.SINGLE,
              size: 1,
              color: "DDDDDD",
            },
            insideVertical: {
              style: BorderStyle.SINGLE,
              size: 1,
              color: "DDDDDD",
            },
          },
        });

        children.push(ratingsTable);
      }

      const footerChildren: Paragraph[] = [];

      if (logoImageData) {
        footerChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: logoImageData.mimeType,
                data: logoImageData.data,
                transformation: {
                  width: 60,
                  height: 60,
                },
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 100 },
          })
        );
      }
      // Create Document
      const doc = new Document({
        creator: "ESG Analysis System",
        title: "ESG Analysis",
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: convertInchesToTwip(0.75),
                  bottom: convertInchesToTwip(0.75),
                  left: convertInchesToTwip(0.75),
                  right: convertInchesToTwip(0.75),
                },
                size: {
                  width: convertInchesToTwip(8.5),
                  height: convertInchesToTwip(11),
                  orientation: PageOrientation.PORTRAIT,
                },
              },
            },
            footers: {
              default: new Footer({
                children: footerChildren,
              }),
            },
            children: children,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `ESG_Analysis_${new Date().getTime()}.docx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error generating Word document:", error);
      alert("Failed to generate Word document. Please try again.");
    } finally {
      setDownloadingWord(false);
    }
  };

  if (loading)
    return <div className="h-64 bg-gray-200 rounded animate-pulse" />;

  if (error)
    return (
      <div className="max-w-4xl mx-auto mt-8">
        <div className="bg-gray-50 border border-gray-300 rounded-xl p-6 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading Data
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );

  const esg = data?.content?.esg_analysis;

  return (
    <div className="bg-white p-6 rounded-2xl shadow border border-gray-300 max-w-4xl mx-auto">
      {/* Header with Download Buttons */}
      <div className="mb-6 border-b border-gray-300 pb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl text-gray-700 font-light">ESG Analysis</h2>

          <div className="flex gap-2">
            <button
              onClick={downloadPDF}
              disabled={downloading}
              className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <RiDownloadLine className="w-4 h-4" />
                  PDF
                </>
              )}
            </button>

            <button
              onClick={downloadWord}
              disabled={downloadingWord}
              className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloadingWord ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <RiDownloadLine className="w-4 h-4" />
                  Word
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Environmental */}
      {esg?.environmental && (
        <div className="mb-6">
          <h3 className="text-md font-light text-gray-500 mb-3 flex items-center gap-2">
            Environmental
          </h3>

          {esg.environmental.climate_change_carbon_emissions
            ?.total_ghg_emissions && (
            <div className="mb-5 pb-4 border-b border-gray-300">
              <p className="text-gray-500 font-medium mb-3 text-sm">
                GHG Emissions (
                {
                  esg.environmental.climate_change_carbon_emissions
                    .total_ghg_emissions.year
                }
                )
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-gray-500 font-medium mb-1 text-sm">
                    Scope 1
                  </p>
                  <p className="text-md font-light text-gray-700">
                    {
                      esg.environmental.climate_change_carbon_emissions
                        .total_ghg_emissions.scope_1
                    }
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium mb-1 text-sm">
                    Scope 2
                  </p>
                  <p className="text-md font-light text-gray-700">
                    {
                      esg.environmental.climate_change_carbon_emissions
                        .total_ghg_emissions.scope_2
                    }
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium mb-1 text-sm">
                    Scope 3
                  </p>
                  <p className="text-md font-light text-gray-700">
                    {
                      esg.environmental.climate_change_carbon_emissions
                        .total_ghg_emissions.scope_3
                    }
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium mb-1 text-sm">
                    Total
                  </p>
                  <p className="text-md font-light text-gray-700">
                    {
                      esg.environmental.climate_change_carbon_emissions
                        .total_ghg_emissions.total
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {esg.environmental.climate_change_carbon_emissions
            ?.carbon_reduction_targets?.net_zero_commitment && (
            <div className="mb-4 pb-4 border-b border-gray-300">
              <p className="text-gray-500 font-medium mb-1 text-sm">
                Net Zero Commitment
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                {
                  esg.environmental.climate_change_carbon_emissions
                    .carbon_reduction_targets.net_zero_commitment
                }
              </p>
            </div>
          )}
        </div>
      )}

      {/* Social */}
      {esg?.social && (
        <div className="mb-6 pb-4 border-b border-gray-300">
          <h3 className="text-md font-light text-gray-500 mb-3 flex items-center gap-2">
            Social
          </h3>

          {esg.social.workforce_labor_practices?.total_employees && (
            <div className="mb-6">
              <p className="text-gray-500 font-medium mb-1 text-sm">
                Total Employees
              </p>
              <p className="text-md font-light text-gray-700">
                {esg.social.workforce_labor_practices.total_employees}
              </p>
            </div>
          )}

          {esg.social.workforce_labor_practices?.gender_diversity && (
            <div className="mb-6">
              <p className="text-gray-500 font-medium mb-3 text-sm">
                Gender Diversity
              </p>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-gray-500 font-medium mb-1 text-sm">
                    Female Employees
                  </p>
                  <p className="text-md font-light text-gray-700">
                    {
                      esg.social.workforce_labor_practices.gender_diversity
                        .female_employees_percentage
                    }
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium mb-1 text-sm">
                    Female Leadership
                  </p>
                  <p className="text-md font-light text-gray-700">
                    {
                      esg.social.workforce_labor_practices.gender_diversity
                        .female_leadership_percentage
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {esg.social.health_safety?.safety_performance && (
            <div>
              <p className="text-gray-500 font-medium mb-3 text-sm">
                Safety Performance (
                {esg.social.health_safety.safety_performance.year})
              </p>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-gray-500 font-medium mb-1 text-sm">
                    LTIFR
                  </p>
                  <p className="text-md font-light text-gray-700">
                    {esg.social.health_safety.safety_performance.ltifr}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium mb-1 text-sm">TRIR</p>
                  <p className="text-md font-light text-gray-700">
                    {esg.social.health_safety.safety_performance.trir}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium mb-1 text-sm">
                    Fatalities
                  </p>
                  <p className="text-md font-light text-gray-700">
                    {esg.social.health_safety.safety_performance.fatalities}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Governance */}
      {esg?.governance && (
        <div className="mb-6">
          <h3 className="text-md font-light text-gray-500 mb-3 flex items-center gap-2">
            Governance
          </h3>

          {esg.governance.board_structure && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-gray-500 font-medium mb-1 text-sm">
                    Board Size
                  </p>
                  <p className="text-md font-light text-gray-700">
                    {esg.governance.board_structure.board_size}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium mb-1 text-sm">
                    Independent Directors
                  </p>
                  <p className="text-md font-light text-gray-700">
                    {esg.governance.board_structure.independent_directors}
                  </p>
                </div>
              </div>

              {esg.governance.board_structure.board_committees && (
                <div>
                  <p className="text-gray-500 font-medium mb-1 text-sm">
                    Board Committees
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {esg.governance.board_structure.board_committees}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ESG Ratings */}
      {esg?.esg_ratings_scores?.external_esg_ratings && (
        <div className="mb-6 pt-4 border-t border-gray-300">
          <h3 className="text-md font-light text-gray-500 mb-3">ESG Ratings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {esg.esg_ratings_scores.external_esg_ratings.map((rating: any) => (
              <div
                key={rating.rating_agency}
                className="flex justify-between items-center"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {rating.rating_agency}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{rating.year}</p>
                </div>
                <div className="text-sm font-light text-blue-700">
                  {rating.score_rating}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Citations */}
      {data?.citations && data.citations.length > 0 && (
        <div className="ml-1 text-gray-700 max-w-4xl text-sm mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Sources</h4>
          <p className="mb-1">
            {data.citations.map((sourceUrl: string, i: number) => {
              const url = new URL(sourceUrl);
              const display = url.hostname.replace("www.", "");
              return (
                <button
                  key={i}
                  onClick={(e) => handleSourceClick(e, sourceUrl)}
                  className="text-xs text-gray-700 border ml-1 mb-1 border-gray-300 font-medium px-2 py-1 rounded-full hover:bg-gray-50 cursor-pointer"
                >
                  {display}
                </button>
              );
            })}
          </p>
        </div>
      )}

      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
};
